import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type {
  KitTemplate,
  CreateZonePayload,
  GeneratePickListPayload,
  PickList,
  PickListListResult,
  PickListScope,
  PickListStatus,
  KitBuild,
  KitDetail,
  KitRow,
  StorageZone,
  UpdatePickListPayload,
  UpdateZonePayload,
} from '@/types/pickList'

/** Shared fetch wrapper — bearer token + `X-Tenant-Id` header (inventoryApi pattern). */
async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId,
      ...init?.headers,
    }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    // Some failures carry structured detail the UI acts on — the expired-lot
    // 409 returns usable substitutes. A plain Error(message) would drop it, so
    // the parsed body rides along on the error.
    throw Object.assign(new Error(message || `Request failed: ${res.status}`), {
      status: res.status,
      body,
    })
  }
  return res.json() as Promise<T>
}

/** Structured detail attached to a failed request, when the server sent any. */
export function errorBody<T>(err: unknown): T | null {
  if (err instanceof Error && 'body' in err) return (err as Error & { body: T }).body ?? null
  return null
}

/** A person a sheet can be assigned to. `name` falls back to the email. */
export interface PickListAssignableUser {
  id: string
  name: string
  email: string
}

export interface ListPickListsParams {
  status?: PickListStatus
  scope?: PickListScope
  procedureId?: string
  q?: string
  page?: number
  pageSize?: number
}

export const pickListsApi = {
  // ─── Pick lists ─────────────────────────────────────────────────────────

  /**
   * Resolve a sheet without saving it. Used by "Print Pick List" so printing
   * never creates a record.
   */
  preview: (tenantId: string, payload: GeneratePickListPayload) =>
    request<PickList>('/inventory/pick-lists/preview', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  generate: (tenantId: string, payload: GeneratePickListPayload) =>
    request<PickList>('/inventory/pick-lists', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  list: (tenantId: string, params: ListPickListsParams = {}) => {
    const qs = new URLSearchParams()
    if (params.status) qs.set('status', params.status)
    if (params.scope) qs.set('scope', params.scope)
    if (params.procedureId) qs.set('procedureId', params.procedureId)
    if (params.q) qs.set('q', params.q)
    if (params.page) qs.set('page', String(params.page))
    if (params.pageSize) qs.set('pageSize', String(params.pageSize))
    const query = qs.toString()
    return request<PickListListResult>(
      `/inventory/pick-lists${query ? `?${query}` : ''}`,
      tenantId,
    )
  },

  get: (tenantId: string, id: string) =>
    request<PickList>(`/inventory/pick-lists/${id}`, tenantId),

  /**
   * Roster for the Assign To picker: this tenant's enabled members, which is
   * exactly the set `update` accepts as an assignee. Gated `pick_lists.generate`
   * server-side, the same permission the assignment itself needs.
   *
   * Deliberately not the equipment or process-step roster: the first needs
   * `equipment.read` and the second returns every enabled platform user
   * regardless of membership, so either would 403 into an empty picker or offer
   * a name the assignment then refuses.
   */
  listAssignableUsers: (tenantId: string) =>
    request<PickListAssignableUser[]>('/inventory/pick-lists/assignable-users', tenantId),

  update: (tenantId: string, id: string, payload: UpdatePickListPayload) =>
    request<PickList>(`/inventory/pick-lists/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /**
   * Record (or clear) the quantity picked for one line.
   *
   * Returns the WHOLE sheet: the line's status, the header's rollup and the
   * progress count all move together, so one response re-renders the view.
   */
  updateLine: (tenantId: string, id: string, lineId: string, qtyPicked: number | null) =>
    request<PickList>(`/inventory/pick-lists/${id}/lines/${lineId}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify({ qtyPicked }),
    }),

  /** Re-resolve demand and replace the lines, bumping the revision in place. */
  revise: (tenantId: string, id: string) =>
    request<PickList>(`/inventory/pick-lists/${id}/revise`, tenantId, { method: 'POST' }),

  /** Audit-only: records that the sheet was printed. */
  recordPrint: (tenantId: string, id: string) =>
    request<{ id: string; printedAt: string }>(
      `/inventory/pick-lists/${id}/printed`,
      tenantId,
      { method: 'POST' },
    ),

  remove: (tenantId: string, id: string) =>
    request<{ id: string }>(`/inventory/pick-lists/${id}`, tenantId, { method: 'DELETE' }),

  // ─── Kits ───────────────────────────────────────────────────────────────

  /** Templates a kit run can start from — kit-only, so no patient is needed. */
  listKitTemplates: (tenantId: string) =>
    request<KitTemplate[]>('/inventory/kits/templates', tenantId),

  /** Start a kit run. Creates the procedure and its build; no Patient record. */
  createKitProcedure: (
    tenantId: string,
    procedureTemplateId: string,
    assigneeId?: string,
    /** Kits to assemble in this run. Multiplies the bill of materials. */
    quantity?: number,
    /** Planned start (YYYY-MM-DD). The Kits page defaults it to today. */
    scheduledDate?: string,
    /**
     * A work order issued outside this system. Optional and never generated
     * here — a kit run is identified by its LOT number.
     */
    workOrderNumber?: string,
    /** Customer the kit is made for (z8nrz7e714). Optional. */
    customerId?: string,
  ) =>
    request<KitBuild>('/inventory/kits/procedures', tenantId, {
      method: 'POST',
      body: JSON.stringify({
        procedureTemplateId,
        ...(assigneeId ? { assigneeId } : {}),
        ...(quantity ? { quantity } : {}),
        ...(scheduledDate ? { scheduledDate } : {}),
        ...(workOrderNumber ? { workOrderNumber } : {}),
        ...(customerId ? { customerId } : {}),
      }),
    }),

  /**
   * Start a kit run — the kit's "Start <form>".
   *
   * A kit is created, then started, then worked, exactly like a transplant, so
   * this is what the run page's Start button calls. It exists instead of the
   * ordinary createSubmission because a kit's subject reference is its supply
   * id, which the submission API's MRN validation rejects by design.
   */
  initiateKitRun: (tenantId: string, procedureId: string, formDefinitionId?: string) =>
    request<KitBuild>(`/inventory/kits/procedures/${procedureId}/initiate`, tenantId, {
      method: 'POST',
      // Which kit form to start: a run can hold several, each its own kit
      // (z8nrz7e11d). Omitted, the server starts the run's first kit form.
      body: JSON.stringify(formDefinitionId ? { formDefinitionId } : {}),
    }),

  /** Kit builds still in progress. */
  listKitForms: (tenantId: string) =>
    request<KitRow[]>('/inventory/kits/forms', tenantId),

  /** Completed kits, with calculated expiry and component lots. */
  listKitInventory: (tenantId: string) =>
    request<KitRow[]>('/inventory/kits/inventory', tenantId),

  // ─── Kit assembly ───────────────────────────────────────────────────────

  /** One finished kit as a record — components, equipment, staffing, answers. */
  getKitDetail: (tenantId: string, buildId: string) =>
    request<KitDetail>(`/inventory/kits/builds/${buildId}/detail`, tenantId),

  /** Read-only. The only consumer is KitBuildRedirect, resolving an old link. */
  getKitBuild: (tenantId: string, id: string) =>
    request<KitBuild>(`/inventory/kits/builds/${id}`, tenantId),

  cancelKitBuild: (tenantId: string, id: string, reason?: string) =>
    request<KitBuild>(`/inventory/kits/builds/${id}/cancel`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ─── Storage zones ──────────────────────────────────────────────────────

  listZones: (tenantId: string, params: { active?: boolean } = {}) => {
    const qs = new URLSearchParams()
    if (params.active !== undefined) qs.set('active', String(params.active))
    const query = qs.toString()
    return request<StorageZone[]>(`/inventory/zones${query ? `?${query}` : ''}`, tenantId)
  },

  createZone: (tenantId: string, payload: CreateZonePayload) =>
    request<StorageZone>('/inventory/zones', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateZone: (tenantId: string, id: string, payload: UpdateZonePayload) =>
    request<StorageZone>(`/inventory/zones/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteZone: (tenantId: string, id: string) =>
    request<{ id: string }>(`/inventory/zones/${id}`, tenantId, { method: 'DELETE' }),

  /** Create the canonical Z1-Z4 zones this tenant is missing. Idempotent. */
  seedDefaultZones: (tenantId: string) =>
    request<{ created: number; skipped: number }>('/inventory/zones/defaults', tenantId, {
      method: 'POST',
    }),
}
